import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Textarea, Select } from 'flowbite-react';
import { v4 as uuidv4 } from 'uuid';
import { useDatabase } from '../../hooks/useDatabase';

/**
 * Form for creating a ResourceReservation. Two modes:
 *
 * - **Block** (no target): the resource is unavailable in this window.
 * - **Pencil-in for a task**: the resource is reserved to support a specific task.
 *
 * Richer scope matchers (tag, project, custom) can be added later — the data
 * model supports `NodeMatcher` here, but the form defaults to a task picker for
 * the initial UI.
 */

const Schema = z
  .object({
    resourceId: z.string().min(1, 'Resource is required'),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    kind: z.enum(['block', 'task']),
    taskId: z.string().optional(),
    remark: z.string().optional(),
  })
  .refine((v) => v.kind !== 'task' || !!v.taskId, {
    message: 'Task is required when kind is "task"',
    path: ['taskId'],
  })
  .refine((v) => v.endDate >= v.startDate, {
    message: 'End date must be on or after start date',
    path: ['endDate'],
  });

type Values = z.infer<typeof Schema>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (command: any) => Promise<void>;
  /** Pre-select a specific resource. */
  defaultResourceId?: string;
  /** Pre-select a specific task (for the "reserve one of these" planner flow). */
  defaultTaskId?: string;
}

const ResourceReservationForm: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  defaultResourceId,
  defaultTaskId,
}) => {
  if (!isOpen) return null;
  const { database } = useDatabase();

  const resources = database
    ? Object.entries(database.resources()).map(([id, r]: [string, any]) => ({
        id,
        name: r.name(),
      }))
    : [];

  const tasks = database
    ? Object.entries(database.tasks()).map(([id, t]: [string, any]) => ({
        id,
        title: t.title(),
      }))
    : [];

  const initialValues: Values = {
    resourceId: defaultResourceId ?? '',
    startDate: '',
    endDate: '',
    kind: defaultTaskId ? 'task' : 'block',
    taskId: defaultTaskId ?? '',
    remark: '',
  };

  const handleSubmit = async (values: Values) => {
    const reservationId = uuidv4();
    const when =
      values.startDate === values.endDate
        ? { type: 'Day', day: values.startDate }
        : {
            type: 'Days',
            start_inclusive: values.startDate,
            end_inclusive: values.endDate,
          };
    const object =
      values.kind === 'task' && values.taskId
        ? { type: 'TaskId', id: values.taskId }
        : null;

    const command = {
      type: 'UpsertResourceReservation',
      reservation: {
        id: reservationId,
        resource_id: values.resourceId,
        when,
        object,
        remark: values.remark ? values.remark : null,
        reserved_by: null,
      },
    };
    await onSubmit(command);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Reserve Resource</h2>

        <Formik
          initialValues={initialValues}
          validationSchema={toFormikValidationSchema(Schema)}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="resourceId">Resource</Label>
                </div>
                <Field as={Select} id="resourceId" name="resourceId">
                  <option value="">— select a resource —</option>
                  {resources.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </Field>
                <ErrorMessage name="resourceId" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="mb-2">
                    <Label htmlFor="startDate">Start date</Label>
                  </div>
                  <Field as={TextInput} type="date" id="startDate" name="startDate" />
                  <ErrorMessage name="startDate" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                <div>
                  <div className="mb-2">
                    <Label htmlFor="endDate">End date</Label>
                  </div>
                  <Field as={TextInput} type="date" id="endDate" name="endDate" />
                  <ErrorMessage name="endDate" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>

              <div>
                <div className="mb-2">
                  <Label htmlFor="kind">Kind</Label>
                </div>
                <Field as={Select} id="kind" name="kind">
                  <option value="block">Block (unavailable)</option>
                  <option value="task">Reserve for a task</option>
                </Field>
              </div>

              {values.kind === 'task' && (
                <div>
                  <div className="mb-2">
                    <Label htmlFor="taskId">Task</Label>
                  </div>
                  <Field as={Select} id="taskId" name="taskId">
                    <option value="">— select a task —</option>
                    {tasks.map((t) => (
                      <option key={t.id} value={t.id}>{t.title}</option>
                    ))}
                  </Field>
                  <ErrorMessage name="taskId" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              )}

              <div>
                <div className="mb-2">
                  <Label htmlFor="remark">Remark</Label>
                </div>
                <Field
                  as={Textarea}
                  id="remark"
                  name="remark"
                  rows={2}
                  placeholder={values.kind === 'block' ? 'e.g. annual calibration' : 'optional notes'}
                />
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>Reserve</Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ResourceReservationForm;
